import { Quote } from 'lucide-react';
import useReveal from '@/hooks/useReveal';

/* NOTE: Replace these placeholder attributions with real Perth chef quotes
   once you have permission — a single real quote is worth more than a
   dozen made-up ones. Update the QUOTES array below or wire this to an
   admin-editable /api/testimonials endpoint later. */
const QUOTES = [
  {
    body: "The Meat Works are the only ones I trust for our steakhouse — the marbling on their wagyu is consistent every single week.",
    who:  "Head Chef",
    place: "Fine-dining restaurant · Perth CBD",
  },
  {
    body: "They cut to our exact spec and hit our loading dock at 6am on the dot. That's what turns a supplier into a partner.",
    who:  "Purchasing Manager",
    place: "Premium supermarket · Perth Metro",
  },
  {
    body: "Halal certification and full traceability meant we could roll them straight into our export line — no re-audit, no delay.",
    who:  "Operations Director",
    place: "Food manufacturer · Malaga",
  },
];

export default function ChefsVoice() {
  const reveal = useReveal();

  return (
    <section
      id="chefs-voice"
      data-testid="chefs-voice-section"
      className="section-light relative py-20 sm:py-28 bg-[#FAFAFA] border-t border-black/10"
    >
      {/* soft top light */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] bg-amber-500/[0.06] rounded-full blur-[140px]"
      />

      <div
        ref={reveal}
        className="mw-reveal relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16"
      >
        <div className="flex items-center gap-3 justify-center mb-5">
          <span className="block w-10 h-px bg-black/25" />
          <span
            data-testid="chefs-voice-eyebrow"
            className="text-[10px] uppercase tracking-[0.32em] text-black/55"
          >
            Chef&apos;s Voice · From the trade
          </span>
          <span className="block w-10 h-px bg-black/25" />
        </div>

        <h2
          data-testid="chefs-voice-headline"
          className="font-display font-light text-center leading-[1.05] tracking-tight text-black/95 text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto"
        >
          What the kitchens
          <em className="not-italic text-[var(--mw-primary)]"> that already trust us </em>
          say.
        </h2>

        <div
          data-testid="chefs-voice-grid"
          className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
        >
          {QUOTES.map((q, i) => (
            <figure
              key={q.body.slice(0, 24)}
              data-testid={`chefs-voice-quote-${i}`}
              className="relative flex flex-col gap-6 border border-black/10 hover:border-[var(--mw-primary)]/60 bg-white p-7 sm:p-8 transition-colors shadow-[0_12px_40px_-20px_rgba(0,0,0,0.15)]"
            >
              <Quote
                size={20}
                className="text-[var(--mw-primary)] shrink-0"
                aria-hidden
              />
              <blockquote className="font-display text-lg sm:text-xl text-black/90 leading-snug tracking-tight">
                &ldquo;{q.body}&rdquo;
              </blockquote>
              <figcaption className="mt-auto pt-5 border-t border-black/10">
                <div className="text-sm text-black font-medium">{q.who}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-black/45">
                  {q.place}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href="#contact"
            data-testid="chefs-voice-cta"
            onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-black/70 hover:text-[var(--mw-primary)] transition-colors"
          >
            Join them — start a trade account
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
